<template>
  <div>
    <div
      :class="[
        'lg:p-4 sm:p-4 p-3 pb-0 relative',
      ]"
    >
      <!-- Full Form View -->
        <div>
          

          <!-- Selection Dropdown -->
          <div
            :class="[
              'relative justify-between flex flex-row items-start sm:mb-8 mb-3 whitespace-nowrap'
            ]"
          >
            <!-- Maximum Stops Dropdown -->
            <div class="flex w-auto rounded-lg transition-colors duration-200 relative">
              <select
                v-model="searchForm.maxStops"
                :class="[
                  'appearance-none rounded-lg border pl-1.5 pr-5 py-1 text-xs font-medium text-white outline-none transition-all hover:bg-white/30 focus:border-white/60 sm:text-sm',
                  props.highlightFields.includes('maxStops')
                    ? 'border-2 border-blue-600 bg-blue-600/50 animate-pulse-once'
                    : 'border-white/30 bg-white/20'
                ]"
                aria-label="Maximum number of stops"
              >
                <option value="direct" class="text-gray-800">{{ t('searchForm.directFlightsOnly') }}</option>
                <option value="1" class="text-gray-800">{{ t('searchForm.stops01') }}</option>
                <option value="2" class="text-gray-800">{{ t('searchForm.stops02') }}</option>
                <option value="3" class="text-gray-800">{{ t('searchForm.stops03') }}</option>
                <option value="4" class="text-gray-800">{{ t('searchForm.stops04') }}</option>
                <option value="any" class="text-gray-800">{{ t('searchForm.stopsAny') }}</option>
              </select>
              <svg class="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 text-white/70" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" /></svg>
            </div>

            <!-- Currency Selection -->
            <div class="w-14">
              <CurrencyDropdown
                v-model="searchForm.currency"
                :currencies="availableCurrencies"
                :has-error="errors.currency"
                @change="handleCurrencySelection"
              />
            </div>
          </div>

          <!-- Search Form -->
          <form @submit.prevent="handleSearch">
            <!-- Multi-city checkbox -->
            <label v-if="!isWeatherMode" class="inline-flex items-center gap-1.5 cursor-pointer text-white/80 select-none mb-1 px-0.5">
              <input
                type="checkbox"
                :checked="searchForm.tripType === 'multi-city'"
                @change="toggleMultiCity($event.target.checked)"
                class="h-3 w-3 rounded border-white/40 text-blue-500 focus:ring-blue-400"
              />
              <span class="text-xs">{{ t('searchForm.multiCity') }}</span>
            </label>
            <!-- Leg 1: Departure and Destination Selection (always shown) -->
            <DepartureDestination
              :departureCities="searchForm.from"
              :destinationCities="searchForm.to"
              @update-departureCities="updateDepartureCities"
              @update-destinationCities="updateDestinationCities"
              :is-error="errors.from"
              :focusElement="focusElement"
              :highlightTo="props.highlightFields.includes('to')"
              :departureLabel="t('searchForm.from')"
              :destinationLabel="t('searchForm.to')"
            />

            <!-- Weather condition buttons – directly under To field -->
            <div v-if="isWeatherMode" class="mt-1.5 flex flex-wrap items-center gap-1.5">
              <button
                v-for="weather in weatherOptions"
                :key="weather.value"
                type="button"
                class="rounded-lg border px-2 py-1 text-xs transition-colors"
                :class="selectedWeather.includes(weather.value) ? 'border-white bg-white/40 font-semibold text-white shadow-sm' : 'border-white/20 bg-white/5 text-white/60 hover:bg-white/15'"
                :aria-pressed="selectedWeather.includes(weather.value)"
                @click="toggleWeather(weather.value)"
              >
                {{ weather.icon }} {{ weather.label }}
              </button>
              <div
                role="button"
                tabindex="0"
                class="ml-auto flex cursor-pointer items-center gap-1 text-xs text-white/70 transition-colors hover:text-white"
                @click="showWeatherAdvanced = !showWeatherAdvanced"
                @keydown.enter="showWeatherAdvanced = !showWeatherAdvanced"
              >
                <span class="sm:hidden">{{ t('searchForm.more') }}</span>
                <span class="hidden sm:inline">{{ t('searchForm.moreOptions') }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 transition-transform" :class="{ 'rotate-90': showWeatherAdvanced }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
              </div>
            </div>

            <!-- Grid Layout for Dates and Other Fields -->
            <div class="mt-1 rounded-lg transition-all">
              <DateRange
                :highlightDates="props.highlightFields.includes('dates')"
                :dateRange="searchForm.tripType === 'multi-city' ? leg1Range : searchForm.range"
                :preserveRange="props.preserveRange"
                :selectedDates="searchForm.tripType === 'multi-city' ? leg1Dates : searchForm.selectedDates"
                :exactDays="searchForm.tripType === 'multi-city' ? leg1Exact : searchForm.exactDays"
                :tripType="searchForm.tripType === 'multi-city' ? 'one-way' : searchForm.tripType"
                :showTripTypeToggle="searchForm.tripType !== 'multi-city'"
                @update-selected-dates="searchForm.tripType === 'multi-city' ? updateLeg1Dates($event) : updateSelectedDates($event)"
                @update-trip-type="updateTripType"
                @update-range="searchForm.tripType === 'multi-city' ? leg1Range = $event : updateRange($event)"
                @update-exact-days="searchForm.tripType === 'multi-city' ? leg1Exact = $event : updateExactDays($event)"
                @popup-closed="handlePopupClosed"
                :is-error="errors.departureDate || errors.returnDate"
                :weatherDays="isWeatherMode ? weatherDays : null"
                :weatherDayOptions="isWeatherMode ? weatherDayOptions : []"
                @update-weather-days="(d) => weatherDays = d"
                :weatherNotice="weatherNoticeText"
                :forecastEndDate="isWeatherMode ? rawForecastEnd : null"
                :nightsReviewActive="nightsReviewMode"
                :defaultExactDates="exactDefault"
                @nights-review="nightsReviewMode = true"
              />

              <!-- Multi-city: extra legs below the standard form -->
              <template v-if="searchForm.tripType === 'multi-city'">
                <div v-for="(leg, idx) in extraLegs" :key="'extra-leg-' + idx" class="mt-3 pt-3 border-t border-white/20 relative">
                  <div class="flex items-center justify-between mb-1.5 px-1">
                    <span class="text-xs font-semibold text-white/80">Flight {{ idx + 2 }}</span>
                    <button
                      type="button"
                      @click="removeLeg(idx)"
                      class="text-white/50 hover:text-red-300"
                      aria-label="Remove leg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <DepartureDestination
                    :departureCities="leg.from"
                    :destinationCities="leg.to"
                    @update-departureCities="(c) => updateLegFrom(idx, c)"
                    @update-destinationCities="(c) => updateLegTo(idx, c)"
                    :is-error="errors.legs && (!leg.from?.length || !leg.to?.length)"
                  />
                  <DateRange
                    class="mt-1"
                    :dateRange="leg.range"
                    :selectedDates="leg.selectedDates"
                    :exactDays="leg.exact"
                    tripType="one-way"
                    :showTripTypeToggle="false"
                    @update-selected-dates="(d) => updateLegDates(idx, d)"
                    @update-range="(r) => leg.range = r"
                    @update-exact-days="(e) => leg.exact = e"
                    @popup-closed="handlePopupClosed"
                    :is-error="errors.legs && (!leg.selectedDates.start || !leg.selectedDates.end)"
                  />
                </div>

                <!-- Add Flight button -->
                <button
                  v-if="extraLegs.length < 5"
                  type="button"
                  @click="addLeg"
                  class="mt-3 flex w-full items-center justify-center rounded-lg border border-dashed border-white/40 py-1.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/20"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add Flight {{ extraLegs.length + 2 }}
                </button>
              </template>

              <!-- Weather: More options (temp + strictness) -->
              <div v-if="isWeatherMode" class="mt-2">
                <transition name="fade">
                  <div v-if="showWeatherAdvanced" class="mt-2 space-y-2">
                    <div class="grid grid-cols-2 gap-2">
                      <label class="text-[11px] text-white/80">
                        Min temperature °C
                        <input v-model.number="weatherMinTemperature" type="number" min="-30" max="50" placeholder="Any" class="mt-0.5 w-full rounded-lg border border-white/30 bg-white/20 px-2 py-1.5 text-sm text-white outline-none placeholder-white/40" />
                      </label>
                      <label class="text-[11px] text-white/80">
                        Max temperature °C
                        <input v-model.number="weatherMaxTemperature" type="number" min="-30" max="50" placeholder="Any" class="mt-0.5 w-full rounded-lg border border-white/30 bg-white/20 px-2 py-1.5 text-sm text-white outline-none placeholder-white/40" />
                      </label>
                    </div>
                    <label class="text-[11px] text-white/80">
                      How strict should weather matching be?
                      <select v-model="weatherMatchMode" class="mt-0.5 w-full rounded-lg border border-white/30 bg-white/20 px-2 py-1.5 text-sm text-white outline-none">
                        <option value="best" class="text-gray-800">Show all — rank by best weather match</option>
                        <option value="mostly" class="text-gray-800">Good match — skip destinations with poor weather</option>
                        <option value="strict" class="text-gray-800">Exact match — only destinations that fully match</option>
                      </select>
                    </label>
                  </div>
                </transition>

                <!-- Info banner -->
                <div v-if="weatherBoundaryExceeded" class="mt-2 rounded-lg border px-2.5 py-2 text-[11px] leading-4 border-amber-200 bg-amber-50 text-amber-800">
                  Weather forecasts beyond {{ rawForecastEnd }} are limited.
                </div>
                <div v-else-if="weatherAvailability.available === false" class="mt-2 rounded-lg border px-2.5 py-2 text-[11px] leading-4 border-white/20 bg-white/10 text-white/80">
                  {{ weatherAvailability.message || 'Weather Search is temporarily unavailable. Please try again soon.' }}
                </div>
              </div>
              <!-- End Weather Options Section -->

            </div>

            <!-- Bottom row: passengers + search button -->
            <div class="sm:mt-4 mt-3 sm:mb-0 mb-3 pb-3 space-y-2">
              <!-- Passenger controls -->
              <div class="flex items-center gap-1.5">
                <!-- Passenger +/- -->
                <div class="flex items-center gap-0.5 rounded-lg border border-white/30 bg-white/20 px-1 py-0.5 hover:bg-white/30 transition-colors">
                  <button
                    type="button"
                    @click="decrementPassengers"
                    :aria-label="t('passengers.removePassenger')"
                    :disabled="searchForm.passengers <= 1"
                    class="w-5 h-5 flex items-center justify-center text-white/80 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" /></svg>
                  </button>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white mx-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  <span class="text-sm font-semibold text-white w-4 text-center">{{ searchForm.passengers }}</span>
                  <button
                    type="button"
                    @click="incrementPassengers"
                    :aria-label="t('passengers.addPassenger')"
                    :disabled="searchForm.passengers >= 9"
                    class="w-5 h-5 flex items-center justify-center text-white/80 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                  </button>
                </div>
                <!-- Luggage icon opens full popup -->
                <button
                  type="button"
                  @click="openPassengerPopup"
                  class="flex items-center gap-1 rounded-lg border border-white/30 bg-white/20 px-2 py-1 text-white/80 hover:text-white hover:bg-white/30 transition-colors"
                  title="Luggage & class options"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  <span class="text-xs">{{ t('searchForm.bags') }}</span>
                </button>
              </div>
              <!-- Inline validation error -->
              <p v-if="notification.type === 'error' && notification.message" class="text-[11px] text-red-300 text-center mb-1">
                {{ notification.message }}
              </p>
              <!-- Close + Search row -->
              <div class="flex items-center gap-2">
              <button
                v-if="closeable"
                type="button"
                @click="emit('close')"
                class="flex items-center justify-center rounded-xl border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/20 transition-colors"
              >
                {{ t('searchForm.close') }}
              </button>
              <button
                type="submit"
                :disabled="isSearching"
                :class="[{ 'opacity-60 cursor-not-allowed': isSearching }]"
                class="flex-1 font-bold inline-flex items-center justify-center bg-blue-600 px-3 py-2.5 border border-transparent text-base rounded-xl shadow-sm text-white hover:bg-blue-700 focus:outline-none transition duration-150 ease-in-out whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg v-if="isSearching" class="animate-spin -ml-0.5 mr-1.5 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="isSearching">{{ t('searchForm.browsing') }}</span>
                <template v-else-if="nightsReviewMode">
                  <span>{{ t('searchForm.looksGood') }}</span>
                </template>
                <template v-else>
                  <span class="sm:hidden">{{ t('searchForm.search') }}</span>
                  <span class="hidden sm:inline">{{ t('searchForm.searchFlights') }}</span>
                </template>
              </button>
              </div>
            </div>
          </form>

          <!-- Passenger Details Popup -->
          <PassengerDetailsPopup
            :isVisible="isPassengerPopupVisible"
            :initialPassengerDetails="searchForm.passengerDetails"
            :initialGlobalClass="globalClass"
            @close="closePassengerPopup"
            @update-passenger-details="updatePassengerDetails"
          />
        </div>

      <!-- Notification Message (non-error) -->
      <div v-if="notification.message && notification.type !== 'error'" :class="notificationClass" class="mt-4 p-2 rounded">
        {{ notification.message }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import DepartureDestination from "@/components/SearchComponents/DepartureDestination.vue";
import DateRange from "@/components/SearchComponents/DateRange.vue";
import CurrencyDropdown from "@/components/SearchComponents/CurrencyDropdown.vue";
import PassengerDetailsPopup from "@/components/SearchComponents/PassengerDetailsPopup.vue"; 
import { useAuthStore } from '@/stores/authStore';
import axios from '@/axios';
import { isConfidentLocation, resolveInitialLocation } from '@/utils/locationResolver';
import { trackOriginAutofill } from '@/utils/originAnalytics';
import { getVariant, getActiveExperiments } from '@/utils/experiments';
import { getVisitorId } from '@/utils/visitorId';
import { closePendingResultsTab, navigatePendingResultsTab, openPendingResultsTab, routeNameToSearchOrigin } from '@/utils/searchNavigation';

// Import weather images
import sunImage from '../assets/icons/sun.png';
import cloudyImage from '../assets/icons/cloudy.png';
import snowyImage from '../assets/icons/snowy.png';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const currentPage = ref(router.currentRoute.value.name || 'search');
const notification = ref({ type: '', message: '' });
const emit = defineEmits(['search-results', 'search-loading', 'search-error', 'updated', 'search-params-update', 'close']);
const focusElement = ref(null);
const isSearching = ref(false);
const nightsReviewMode = ref(false);
const props = defineProps({
  passedSearchParams: {
    type: [String, Object],
    default: null
  },
  initialCurrency: {
    type: String,
    default: null
  },
  searchMode: {
    type: String,
    default: null
  },
  preserveRange: {
    type: Boolean,
    default: false
  },
  openInNewTab: {
    type: Boolean,
    default: false
  },
  highlightFields: {
    type: Array,
    default: () => []
  },
  closeable: {
    type: Boolean,
    default: false
  },
});

// --- Weather Options Setup ---
const selectedWeather = ref(['clear_weather']);
const weatherOptions = [
  { value: 'clear_weather', icon: '☀️', img: sunImage, label: 'Sunny' },
  { value: 'cloud_weather', icon: '☁️', img: cloudyImage, label: 'Cloudy' },
  { value: 'snow_weather', icon: '❄️', img: snowyImage, label: 'Snow' }
];
const weatherMinTemperature = ref(null);
const weatherMaxTemperature = ref(null);
const showWeatherAdvanced = ref(false);
const weatherMatchMode = ref('best');
const weatherDays = ref(3);
const weatherDayOptions = [1, 3, 5, 7];
const weatherAvailability = ref({});
const isWeatherMode = computed(() => currentPage.value === 'WeatherSearch' || props.searchMode === 'weather');
const activeWeatherDuration = computed(() => {
  if (searchForm.value.tripType === 'one-way') return weatherDays.value;
  return Math.min(16, Number(searchForm.value.range?.max || 5) + 1);
});
const rawForecastEnd = computed(() => {
  if (!isWeatherMode.value) return null;
  const d = new Date();
  d.setDate(d.getDate() + 15);
  return d.toISOString().slice(0, 10);
});
const latestWeatherStart = computed(() => {
  // Prefer the server forecast window; fall back to a client-side 16-day window
  // so the guidance still shows even if availability hasn't loaded yet.
  const end = weatherAvailability.value.forecast_end
    ? new Date(`${weatherAvailability.value.forecast_end}T12:00:00`)
    : (() => { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() + 16); return d; })();
  end.setDate(end.getDate() - activeWeatherDuration.value + 1);
  return end.toISOString().slice(0, 10);
});
const weatherBoundaryExceeded = computed(() => {
  if (!latestWeatherStart.value || !searchForm.value.selectedDates?.start) return false;
  const selectedEnd = _toIsoDate(searchForm.value.selectedDates.end || searchForm.value.selectedDates.start);
  return Boolean(selectedEnd && selectedEnd > latestWeatherStart.value);
});
const weatherNoticeText = computed(() => {
  if (!isWeatherMode.value) return '';
  if (weatherBoundaryExceeded.value) {
    return `Weather forecasts beyond ${rawForecastEnd.value} are limited.`;
  }
  return '';
});

const fetchWeatherAvailability = async () => {
  if (!isWeatherMode.value) return;
  try {
    const response = await axios.get('/search/weather/availability/');
    weatherAvailability.value = response.data || {};
  } catch {
    weatherAvailability.value = { message: 'Weather Search is temporarily unavailable. Please try again soon.' };
  }
};

const applyWeatherParams = (params) => {
  const preferences = params?.weather_preferences || {};
  if (Array.isArray(preferences.conditions)) selectedWeather.value = [...preferences.conditions];
  if (preferences.min_temperature !== null && preferences.min_temperature !== undefined) weatherMinTemperature.value = preferences.min_temperature;
  if (preferences.max_temperature !== null && preferences.max_temperature !== undefined) weatherMaxTemperature.value = preferences.max_temperature;
  if (preferences.match_mode) weatherMatchMode.value = preferences.match_mode;
  if (params?.weather_days) weatherDays.value = params.weather_days;
};

const toggleWeather = (value) => {
  if (selectedWeather.value.includes(value)) {
    selectedWeather.value = selectedWeather.value.filter(v => v !== value);
  } else {
    selectedWeather.value.push(value);
  }
};


// Reactive state for the search form
// Prefilled dates are what let a first-time visitor press Search without first
// opening the date picker. Empty dates fail validateForm(), so an untouched
// form is a hard stop at the worst step in the funnel: only 3.8% of visitors
// ever reach a search, while 36.5% of those who do go on to click through to
// book.
//
// 21 days is deliberate, not arbitrary. Conversion falls away as the window
// widens (0-7d: 22.1%, 8-14d: 17.8%, 15-21d: 19.2%, 22-31d: 15.4%, 32+d: 14.0%)
// and Kiwi returns weaker results over long ranges - but a window of only a few
// days throws away the flexible-dates proposition the product is built on, and
// the narrow-window conversion is partly selection bias: people who pick an
// exact date already know where they are going.
//
// Weather mode is excluded: its forecast horizon is ~16 days, so a 21-day
// window would immediately trip the "forecasts beyond X are limited" notice.
const DEFAULT_DATE_WINDOW_DAYS = 21;

const searchFormVariant = isWeatherMode.value ? 'control' : getVariant('search_form_dates');
const exactDefault = searchFormVariant === 'exact_default';

const defaultSelectedDates = () => {
  if (exactDefault) {
    return { start: null, end: null };
  }
  const routeName = String(currentPage.value || '').replace(/Locale$/, '');
  if (props.searchMode === 'weather' || routeName === 'WeatherSearch') {
    return { start: null, end: null };
  }
  const start = new Date();
  start.setHours(12, 0, 0, 0);
  start.setDate(start.getDate() + 1);
  const end = new Date(start);
  end.setDate(end.getDate() + DEFAULT_DATE_WINDOW_DAYS);
  return { start, end };
};

const searchForm = ref({
  from: [],
  to: [],
  selectedDates: defaultSelectedDates(),
  tripType: "return",
  passengers: 1,
  detailsOption: "default",
  directFlights: false,
  maxStops: 'direct',
  currency: props.initialCurrency || 'USD',
  searchType: 'Basic',
  range: { min: 3, max: 5 },
  exactDays: exactDefault,
  passengerDetails: [{
    class: 'economy',
    noBags: true,
    bags: []
  }],
  // Added weather field to store the selected weather types
  weather: []
});

// List of available currencies
const availableCurrencies = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN', 
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL', 
  'BSD', 'BTC', 'BTN', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLF', 
  'CLP', 'CNY', 'COP', 'CRC', 'CUC', 'CUP', 'CVE', 'CZK', 'DJF', 'DKK', 
  'DOP', 'DZD', 'EEK', 'EGP', 'ERN', 'ETB', 'EUR', 'FJD', 'FKP', 'GBP', 
  'GEL', 'GGP', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD', 'HNL', 
  'HRK', 'HTG', 'HUF', 'IDR', 'ILS', 'IMP', 'INR', 'IQD', 'IRR', 'ISK', 
  'JEP', 'JMD', 'JOD', 'JPY', 'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 
  'KWD', 'KYD', 'KZT', 'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LTL', 'LVL', 
  'LYD', 'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRO', 'MTL', 
  'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN', 'NAD', 'NGN', 'NIO', 'NOK', 
  'NPR', 'NZD', 'OMR', 'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG', 
  'QAR', 'QUN', 'RON', 'RSD', 'RUB', 'RWF', 'SAR', 'SBD', 'SCR', 'SDG', 
  'SEK', 'SGD', 'SHP', 'SLL', 'SOS', 'SRD', 'STD', 'SVC', 'SYP', 'SZL', 
  'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS', 'UAH', 
  'UGX', 'USD', 'UYU', 'UZS', 'VEF', 'VND', 'VUV', 'WST', 'XAF', 'XCD', 
  'XOF', 'XPF', 'YER', 'ZAR', 'ZMK', 'ZMW', 'ZWL'
];

// Currency detection functions
const detectCurrencyFromBrowser = () => {
  const userLocale = navigator.language || 'en-US';
  const region = userLocale.split('-')[1] || 'US';
  
  // Common region to currency mappings
  const regionToCurrency = {
    US: 'USD',
    GB: 'GBP',
    UK: 'GBP',
    EU: 'EUR',
    JP: 'JPY',
    AU: 'AUD',
    CA: 'CAD',
    CH: 'CHF',
    CN: 'CNY',
    SE: 'SEK',
    NZ: 'NZD',
    RU: 'RUB',
    IN: 'INR',
    BR: 'BRL',
    ZA: 'ZAR',
    AE: 'AED',
    // Add more mappings as needed
  };
  
  return regionToCurrency[region] || 'USD';
};

const detectUserCurrency = async (approximateLocation) => {
  // If authenticated, prefer the user's saved preferred currency
  const authStore = useAuthStore();
  if (authStore.isAuthenticated && authStore.user?.preferred_currency) {
    const userCurrency = authStore.user.preferred_currency;
    if (availableCurrencies.includes(userCurrency)) {
      console.log('Using auth user preferred_currency:', userCurrency);
      return userCurrency;
    }
  }

  // Respect a currency the user picked themselves
  const isManualChoice = localStorage.getItem('currencySource') === 'manual';
  const preferredCurrency = localStorage.getItem('preferredCurrency');
  if (isManualChoice && preferredCurrency && availableCurrencies.includes(preferredCurrency)) {
    console.log('Using manually selected currency:', preferredCurrency);
    return preferredCurrency;
  }

  // Otherwise follow the detected location so currency matches the departure country. A shaky
  // detection is skipped here too: showing prices in the wrong currency is harder to notice
  // than a wrong departure city.
  const ipCurrency = isConfidentLocation(approximateLocation) ? approximateLocation?.currency : null;
  if (ipCurrency && availableCurrencies.includes(ipCurrency)) {
    console.log('Currency detected from approximate location:', ipCurrency);
    return ipCurrency;
  }

  // Fall back to a previously stored value when detection is unavailable
  const savedCurrency = preferredCurrency || localStorage.getItem('currency');
  if (savedCurrency && availableCurrencies.includes(savedCurrency)) {
    console.log('Using saved currency from localStorage:', savedCurrency);
    return savedCurrency;
  }
  
  // Fall back to browser detection
  console.log('Falling back to browser-based currency detection...');
  const browserCurrency = detectCurrencyFromBrowser();
  console.log('Currency detected from browser:', browserCurrency);
  return browserCurrency;
};

let isApplyingDetectedCurrency = false;

const saveCurrencyPreference = (currency, source = 'auto') => {
  // Save to both keys for consistency
  localStorage.setItem('currency', currency);
  localStorage.setItem('preferredCurrency', currency);
  if (source === 'manual') {
    localStorage.setItem('currencySource', 'manual');
  }
};

const handleCurrencySelection = (currency) => {
  saveCurrencyPreference(currency, 'manual');
  const authStore = useAuthStore();
  authStore.savePreferredCurrency(currency);
};

onMounted(async () => {
  if (!isWeatherMode.value) {
    const exposureKey = 'pfExposed:search_form_dates';
    let alreadyExposed = false;
    try {
      alreadyExposed = !!sessionStorage.getItem(exposureKey);
      sessionStorage.setItem(exposureKey, '1');
    } catch (_) {
      /* sessionStorage blocked — count the exposure anyway */
    }
    if (!alreadyExposed) {
      axios.post('/analytics/track-event/', {
        event_type: 'experiment_exposure',
        visitor_id: getVisitorId(),
        extra: { experiment: 'search_form_dates', variant: searchFormVariant },
      }).catch(() => {});
    }
  }

  const approximateLocation = await resolveInitialLocation();
  const autofillOrigin = isConfidentLocation(approximateLocation) ? approximateLocation : null;

  if (autofillOrigin && searchForm.value.from.length === 0) {
    trackOriginAutofill(autofillOrigin, 'search-form');
    searchForm.value.from = [{
      id: autofillOrigin.city.code,
      display: autofillOrigin.city.display_name || autofillOrigin.city.name,
      type: 'city',
      code: autofillOrigin.city.code,
      source: autofillOrigin.accuracy || 'approximate-ip',
    }];
  }

  // Detect and set user currency first, unless the caller supplied one explicitly.
  const detectedCurrency = props.initialCurrency || await detectUserCurrency(approximateLocation);
  
  // Always apply the detected currency and save auto-detected values.
  if (detectedCurrency) {
    isApplyingDetectedCurrency = true;
    searchForm.value.currency = detectedCurrency;
    if (!props.initialCurrency) saveCurrencyPreference(detectedCurrency);
    setTimeout(() => { isApplyingDetectedCurrency = false; }, 0);
    console.log('Currency set from detection:', detectedCurrency);
  }
  
  // Listen for currency updates from geolocation
  window.addEventListener('currency-updated', (event) => {
    if (event.detail && event.detail.currency) {
      console.log('Currency updated from geolocation:', event.detail.currency);
      searchForm.value.currency = event.detail.currency;
    }
  });
  
  // Check for URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const searchUuid = urlParams.get('searchUuid');
  const newParamsString = urlParams.get('searchParams');
  
  // If searchUuid is present, fetch params from API
  if (searchUuid) {
    try {
      const response = await axios.get(`/search/search_history/${searchUuid}/`);
      const newParams = response.data;
      if (newParams) {
        // Save the current currency before applying
        const savedCurrency = searchForm.value.currency;
        console.log('Applying params from searchUuid:', searchUuid);
        
        // Apply parameters from API
        searchForm.value = { ...searchForm.value, ...newParams };
        applyWeatherParams(newParams);
        if ((newParams.tripType || newParams.trip_type) === 'multi-city') hydrateMultiCityFromParams(newParams);
        
        // Restore the saved currency
        searchForm.value.currency = savedCurrency;
        console.log('Applied search params from UUID');
      }
    } catch (error) {
      console.error('Error fetching search params by UUID:', error);
    }
    return;
  }
  
  // Fall back to searchParams in URL (backward compatibility)
  if (isWeatherMode.value) await fetchWeatherAvailability();
  if (!newParamsString) { return; }
  
  try {
    const newParams = JSON.parse(newParamsString);
    if (!newParams) return;
    
    // Save the current currency before applying URL parameters
    const savedCurrency = searchForm.value.currency;
    console.log('Saving currency before URL params:', savedCurrency);
    
    // Apply URL parameters
    searchForm.value = { ...newParams };
    applyWeatherParams(newParams);
    if ((newParams.tripType || newParams.trip_type) === 'multi-city') hydrateMultiCityFromParams(newParams);
    
    // Restore the saved currency
    searchForm.value.currency = savedCurrency;
    console.log('Restored currency after URL params:', searchForm.value.currency);
  } catch (error) {
    console.error('Error parsing search parameters:', error);
  }
});

// Keep passengerDetails in sync with the number of passengers
watch(
  () => searchForm.value.passengers,
  (newVal) => {
    const currentLength = searchForm.value.passengerDetails.length;
    if (newVal > currentLength) {
      for (let i = currentLength; i < newVal; i++) {
        searchForm.value.passengerDetails.push({
          class: 'economy',
          noBags: true,
          bags: []
        });
      }
    } else if (newVal < currentLength) {
      searchForm.value.passengerDetails.splice(newVal);
    }
  },
  { immediate: true }
);

watch(
  () => router.currentRoute.value.name,
  (newVal) => {
    currentPage.value = newVal;
    if (isWeatherMode.value) {
      if (searchForm.value.tripType === 'multi-city') searchForm.value.tripType = 'one-way';
      fetchWeatherAvailability();
    }
  },
  { immediate: true }
);

watch(
  () => props.searchMode,
  () => {
    if (isWeatherMode.value) {
      if (searchForm.value.tripType === 'multi-city') searchForm.value.tripType = 'one-way';
      fetchWeatherAvailability();
    }
  },
  { immediate: true }
);

const errors = ref({
  from: false,
  departureDate: false,
  returnDate: false,
  currency: false,
  legs: false,
});

const notificationClass = computed(() => {
  return {
    'bg-red-100 text-red-800': notification.value.type === 'error',
    'bg-green-100 text-green-800': notification.value.type === 'success',
    'bg-blue-100 text-blue-800': notification.value.type === 'info'
  };
});

const updateDepartureCities = (cities) => {
  searchForm.value.from = cities;
  if (cities.length > 0) {
    errors.value.from = false;
  }
};

const updateDestinationCities = (cities) => {
  searchForm.value.to = cities;
  if (searchForm.value.tripType === 'multi-city') {
    const next = extraLegs.value[0];
    if (next && !next.from?.length && cities?.length && !isAnywhere(cities)) next.from = [...cities];
    serializeMcState();
    return;
  }
  emit('search-params-update', { to: cities });
};

const updateTripType = (type) => {
  searchForm.value.tripType = type;
};

// === Multi-city state & helpers ===
// Leg 1 dates live separately so toggling multi-city does not corrupt
// the basic-search selectedDates / range / exactDays state.
const leg1Dates = ref({ start: null, end: null });
const leg1Range = ref({ min: 3, max: 5 });
const leg1Exact = ref(false);

// Legs 2..N (each mirrors leg1's shape)
const extraLegs = ref([]);

const _toIsoDate = (d) => {
  if (!d) return null;
  if (typeof d === 'string') return d.split('T')[0];
  try { return new Date(d).toISOString().split('T')[0]; } catch { return null; }
};

const isAnywhere = (cities) => {
  if (!cities || !cities.length) return false;
  return cities.some(c => c.code === 'anywhere' || c.display?.toLowerCase() === 'anywhere');
};

// eslint-disable-next-line no-unused-vars
const toggleMultiCity = (enabled) => {
  if (enabled) {
    searchForm.value.tripType = 'multi-city';
    // Pre-fill leg 1 dates from existing basic-search selection if present
    if (!leg1Dates.value.start && searchForm.value.selectedDates?.start) {
      leg1Dates.value = {
        start: searchForm.value.selectedDates.start,
        end: searchForm.value.selectedDates.end || searchForm.value.selectedDates.start,
      };
    }
    // Seed first extra leg: From = prev To (if not Anywhere), To = empty
    if (extraLegs.value.length === 0) {
      extraLegs.value.push({
        from: !isAnywhere(searchForm.value.to) && searchForm.value.to ? [...searchForm.value.to] : [],
        to: [],
        selectedDates: { start: null, end: null },
        range: { min: 3, max: 5 },
        exact: false,
      });
    }
  } else {
    searchForm.value.tripType = 'one-way';
    emit('search-params-update', { ...searchForm.value, tripType: 'one-way', legSelections: [] });
  }
  errors.value.legs = false;
};

const addLeg = () => {
  const prev = extraLegs.value[extraLegs.value.length - 1];
  extraLegs.value.push({
    from: prev?.to && !isAnywhere(prev.to) ? [...prev.to] : [],
    to: [],
    selectedDates: { start: null, end: null },
    range: { min: 3, max: 5 },
    exact: false,
  });
};

const removeLeg = (idx) => {
  extraLegs.value.splice(idx, 1);
  if (extraLegs.value.length === 0) {
    searchForm.value.tripType = 'return';
  }
};

const updateLegFrom = (idx, cities) => {
  extraLegs.value[idx].from = cities;
};

const updateLegTo = (idx, cities) => {
  extraLegs.value[idx].to = cities;
  // Auto-update next leg's From (if exists), but only if current To is not Anywhere
  const nextIdx = idx + 1;
  if (extraLegs.value[nextIdx] && !isAnywhere(cities)) {
    extraLegs.value[nextIdx].from = cities ? [...cities] : [];
  }
};

const updateLeg1Dates = (dates) => {
  leg1Dates.value = dates;
};

// Hydrate multi-city state from search params (used when results page passes params back)
function hydrateMultiCityFromParams(params) {
  const selections = params.legSelections || params.leg_selections;
  const rawLegs = params.legs;
  const source = selections || rawLegs;
  if (!source || !source.length) return;
  if (selections && JSON.stringify(selections) === JSON.stringify(buildLegSelections())) return;

  // Hydrate leg 1
  const first = source[0];
  leg1Dates.value = {
    start: first.dateFrom || first.date_from || null,
    end: first.dateTo || first.date_to || null,
  };
  if (selections && first.from) {
    const normalizeCities = (arr) => (Array.isArray(arr) ? arr : []).map(c => ({
      type: c.type || 'city', id: c.id || c.code, code: c.code, display: c.display || c.code,
    }));
    searchForm.value.from = normalizeCities(first.from);
    searchForm.value.to = normalizeCities(first.to);
  }

  // Hydrate extra legs
  extraLegs.value = source.slice(1).map(leg => {
    const normalizeCities = (arr) => (Array.isArray(arr) ? arr : []).map(c => ({
      type: c.type || 'city', id: c.id || c.code, code: c.code || c.id, display: c.display || c.code || c.id,
    }));
    return {
      from: selections ? normalizeCities(leg.from) : [],
      to: selections ? normalizeCities(leg.to) : [],
      selectedDates: {
        start: leg.dateFrom || leg.date_from || null,
        end: leg.dateTo || leg.date_to || null,
      },
      range: { min: 3, max: 5 },
      exact: false,
    };
  });
}

const updateLegDates = (idx, dates) => {
  extraLegs.value[idx].selectedDates = dates;
};

// Sync multi-city editing state to parent so desktop↔mobile resize preserves legs
const serializeCities = (arr) => (arr || []).map(item => ({ type: item.type, id: item.id || item.code, code: item.code, display: item.display }));
const buildLegSelections = () => [
  { from: serializeCities(searchForm.value.from), to: serializeCities(searchForm.value.to), dateFrom: leg1Dates.value.start, dateTo: leg1Dates.value.end },
  ...extraLegs.value.map(l => ({ from: serializeCities(l.from), to: serializeCities(l.to), dateFrom: l.selectedDates?.start, dateTo: l.selectedDates?.end })),
];
const serializeMcState = () => {
  if (searchForm.value.tripType !== 'multi-city') return;
  const legSelections = buildLegSelections();
  const passedLegSelections = props.passedSearchParams?.legSelections || props.passedSearchParams?.leg_selections || [];
  if (JSON.stringify(legSelections) === JSON.stringify(passedLegSelections)) return;
  emit('search-params-update', { ...searchForm.value, tripType: 'multi-city', legSelections });
};
watch([extraLegs, leg1Dates, () => searchForm.value.from, () => searchForm.value.to], serializeMcState, { deep: true });

// Watcher for passed search parameters
watch(
  () => props.passedSearchParams,
  (newVal) => {
    if (newVal) {
      try {
        // Save the current currency before applying new params
        const currentCurrency = searchForm.value.currency;

        const parsedParams = typeof newVal === 'string'
          ? JSON.parse(newVal)
          : newVal;

        // Apply new parameters
        searchForm.value = { ...searchForm.value, ...parsedParams };
        applyWeatherParams(parsedParams);

        // Hydrate multi-city state if applicable
        const tt = parsedParams.tripType || parsedParams.trip_type;
        if (tt === 'multi-city') {
          hydrateMultiCityFromParams(parsedParams);
        }

        // Restore the user's currency preference
        if (currentCurrency) {
          searchForm.value.currency = currentCurrency;
        }

        // NOTE: Do NOT emit 'search-params-update' here to avoid infinite loop
        // The parent already knows about these params since it passed them
      } catch (error) {
        console.error('Error parsing search parameters:', error);
      }
    }
  },
  { deep: true, immediate: true }
);

const updateRange = (range) => {
  searchForm.value.range = range;
  emit('search-params-update', { range });
};

const updateExactDays = (days) => {
  searchForm.value.exactDays = days;
  emit('search-params-update', { exactDays: days });
};

const updateSelectedDates = (dates) => {
  searchForm.value.selectedDates = dates;
  if (dates.start) {
    errors.value.departureDate = false;
  }
  if (dates.end) {
    errors.value.returnDate = false;
  }
  emit('search-params-update', { selectedDates: dates });
};

const validateForm = () => {
  let isValid = true;

  // Multi-city validation
  if (searchForm.value.tripType === 'multi-city') {
    errors.value.legs = false;
    errors.value.from = false;
    const l1Start = _toIsoDate(leg1Dates.value.start);
    const l1End = _toIsoDate(leg1Dates.value.end);
    if (!searchForm.value.from?.length || !searchForm.value.to?.length) {
      errors.value.from = true;
      errors.value.legs = true;
      isValid = false;
    }
    if (!l1Start || !l1End || l1End < l1Start) {
      errors.value.legs = true;
      isValid = false;
    }
    let prevStart = l1Start;
    for (const leg of extraLegs.value) {
      const s = _toIsoDate(leg.selectedDates?.start);
      const e = _toIsoDate(leg.selectedDates?.end);
      if (!leg.from?.length || !leg.to?.length || !s || !e || e < s) {
        errors.value.legs = true; isValid = false; break;
      }
      if (prevStart && s < prevStart) {
        errors.value.legs = true; isValid = false; break;
      }
      prevStart = s;
    }
    if (!searchForm.value.currency) { errors.value.currency = true; isValid = false; }
    return isValid;
  }

  if (!searchForm.value.from || searchForm.value.from.length === 0) {
    errors.value.from = true;
    isValid = false;
  } else {
    errors.value.from = false;
  }
  if (!searchForm.value.selectedDates.start) {
    errors.value.departureDate = true;
    isValid = false;
  } else {
    errors.value.departureDate = false;
  }
  if (searchForm.value.tripType === 'return' && !searchForm.value.selectedDates.end) {
    errors.value.returnDate = true;
    isValid = false;
  } else {
    errors.value.returnDate = false;
  }
  if (!searchForm.value.currency) {
    errors.value.currency = true;
    isValid = false;
  } else {
    errors.value.currency = false;
  }
  return isValid;
};

const handleSearch = () => {
  if (nightsReviewMode.value) {
    nightsReviewMode.value = false;
    return;
  }
  notification.value = {type: '', message: ''};
  if (!validateForm()) {
    const missing = [];
    if (errors.value.from) missing.push('departure city');
    if (errors.value.departureDate) {
      missing.push(searchForm.value.exactDays
        ? (searchForm.value.tripType === 'one-way' ? t('searchForm.flyOn') : t('searchForm.departOn'))
        : t('searchForm.departAfter'));
    }
    if (errors.value.returnDate) missing.push(searchForm.value.exactDays ? t('searchForm.returnOn') : t('searchForm.returnBefore'));
    if (errors.value.currency) missing.push('currency');
    if (errors.value.legs) missing.push('flight legs');
    console.warn('[SearchForm] Validation failed. Missing:', missing, 'Form state:', JSON.parse(JSON.stringify(searchForm.value)));
    notification.value = {
      type: 'error',
      message: missing.length
        ? `Please fill out: ${missing.join(', ')}.`
        : 'Please fill out all required fields.',
    };
    return;
  }

  if (isWeatherMode.value && weatherAvailability.value.available === false) {
    notification.value = {
      type: 'error',
      message: weatherAvailability.value.message || 'Weather Search is temporarily unavailable. Please try again soon.',
    };
    return;
  }

  const resultWindow = openPendingResultsTab(props.openInNewTab);

  // For one-way flights with only start date, set end = start
  if (searchForm.value.tripType === 'one-way' && searchForm.value.selectedDates.start && !searchForm.value.selectedDates.end) {
    searchForm.value.selectedDates.end = searchForm.value.selectedDates.start;
  }

  // Add selected weather options to the searchForm
  searchForm.value.weather = selectedWeather.value;

  emit('search-loading', true);
  isSearching.value = true;
  const searchParams = { ...searchForm.value };
  const serializeSelections = (items) => (items || []).map(item => ({
    type: item.type,
    id: item.id,
    code: item.code,
    display: item.display,
  }));
  searchParams.fromSelections = serializeSelections(searchForm.value.from);
  searchParams.toSelections = serializeSelections(searchForm.value.to);
  if (isWeatherMode.value) {
    searchParams.searchType = 'Weather';
    searchParams.weather_preferences = {
      conditions: selectedWeather.value,
      min_temperature: weatherMinTemperature.value,
      max_temperature: weatherMaxTemperature.value,
      match_mode: weatherMatchMode.value,
    };
    searchParams.weather_days = searchForm.value.tripType === 'one-way' ? weatherDays.value : null;
  }

  // Multi-city: build legs payload (per-leg date range) and skip the standard from/to/date logic
  if (searchForm.value.tripType === 'multi-city') {
    const uniqueSelections = (arr) => {
      const seen = new Set();
      return (arr || []).filter(item => {
        const code = String(item?.code || item?.id || '').replace(/-/g, '').trim().toUpperCase();
        const type = String(item?.type || 'city').toLowerCase();
        if (!code || seen.has(`${type}:${code}`)) return false;
        seen.add(`${type}:${code}`);
        return true;
      });
    };
    const joinCodes = (arr) => uniqueSelections(arr).map(item => String(item.code || item.id || '').replace(/-/g, '').toUpperCase()).join(',');
    const serializeCities = (arr) => uniqueSelections(arr).map(item => ({
      type: item.type,
      id: item.id || item.code,
      code: String(item.code || item.id || '').replace(/-/g, '').toUpperCase(),
      display: item.display,
    }));
    const allLegs = [
      {
        from: joinCodes(searchForm.value.from),
        to: joinCodes(searchForm.value.to),
        dateFrom: _toIsoDate(leg1Dates.value.start),
        dateTo: _toIsoDate(leg1Dates.value.end),
      },
      ...extraLegs.value.map(l => ({
        from: joinCodes(l.from),
        to: joinCodes(l.to),
        dateFrom: _toIsoDate(l.selectedDates?.start),
        dateTo: _toIsoDate(l.selectedDates?.end),
      })),
    ];
    // Include full city selections so the results page can reconstruct the form
    const legSelections = [
      {
        from: serializeCities(searchForm.value.from),
        to: serializeCities(searchForm.value.to),
        dateFrom: _toIsoDate(leg1Dates.value.start),
        dateTo: _toIsoDate(leg1Dates.value.end),
      },
      ...extraLegs.value.map(l => ({
        from: serializeCities(l.from),
        to: serializeCities(l.to),
        dateFrom: _toIsoDate(l.selectedDates?.start),
        dateTo: _toIsoDate(l.selectedDates?.end),
      })),
    ];
    searchParams.tripType = 'multi-city';
    searchParams.legs = allLegs;
    searchParams.legSelections = legSelections;
    delete searchParams.from;
    delete searchParams.to;

    // Human-readable destination chain (e.g., "EDI → CDG → JFK → EDI")
    const codePreview = (cities) => (cities && cities[0]?.display) || (cities && cities[0]?.code) || '';
    const chain = [codePreview(searchForm.value.from), codePreview(searchForm.value.to)];
    extraLegs.value.forEach(l => chain.push(codePreview(l.to)));
    searchParams.destination_display_name = chain.filter(Boolean).join(' → ');
    // Passenger details still apply
    if (searchForm.value.detailsOption === 'customise') {
      searchForm.value.passengerDetails = searchForm.value.passengerDetails.map(p => ({
        class: p.class || 'economy', noBags: p.noBags, bags: p.bags || [],
      }));
    } else {
      searchForm.value.passengerDetails = Array.from({ length: searchForm.value.passengers }, () => ({
        class: globalClass.value || 'economy', noBags: true, bags: [],
      }));
    }
    searchParams.passengerDetails = searchForm.value.passengerDetails;
    // Emit multi-city params with legs data
    emit('search-params-update', { ...searchParams });
    sendDataToBackend(searchParams, resultWindow).finally(() => { emit('search-loading', false); isSearching.value = false; });
    return;
  }

  if (Array.isArray(searchForm.value.from)) {
    searchParams.from = searchForm.value.from.map(item => item.code?.replace(/-/g, '')).join(",");
  }
  if (Array.isArray(searchForm.value.to) && searchForm.value.to.length > 0) {
    searchParams.to = searchForm.value.to.map(item => item.code?.replace(/-/g, '')).join(",");
    searchParams.selection_to = searchParams.to;
    searchParams.selection_to_json = [...searchParams.toSelections];
    // Calculate destination_display_name
    if (searchForm.value.to.length >= 500) {
      searchParams.destination_display_name = 'Anywhere';
    } else {
      searchParams.destination_display_name = searchForm.value.to.slice(0, 3).map(item => item.display).join(', ');
    }
  } else {
    // Explicitly clear legacy destination fields so a results-page search cannot
    // resurrect the previous country when the user chooses Anywhere.
    delete searchParams.to;
    searchParams.cities_to = '';
    searchParams.selection_to = '';
    searchParams.selection_to_json = [];
    searchParams.toSelections = [];
    searchParams.destination_display_name = 'Anywhere';
  }

  emit('search-params-update', {
    ...searchParams,
    from: [...(searchForm.value.from || [])],
    to: [...(searchForm.value.to || [])],
    selectedDates: { ...searchForm.value.selectedDates },
    range: { ...searchForm.value.range },
  });

  if (searchForm.value.detailsOption === 'customise') {
    searchForm.value.passengerDetails = searchForm.value.passengerDetails.map(passenger => ({
      class: passenger.class || 'economy',
      noBags: passenger.noBags,
      bags: passenger.bags || [],
    }));
  } else {
    // If passenger_details was loaded from search history, use it instead of resetting
    if (searchForm.value.passenger_details && Array.isArray(searchForm.value.passenger_details)) {
      searchForm.value.passengerDetails = searchForm.value.passenger_details;
    } else {
      searchForm.value.passengerDetails = Array.from({length: searchForm.value.passengers}, () => ({
        class: globalClass.value || 'economy',
        noBags: true,
        bags: [],
      }));
    }
  }

  sendDataToBackend(searchParams, resultWindow)
    .finally(() => {
      console.log('Search parameters:', searchParams);
      emit('search-loading', false);
      isSearching.value = false;
    });
};

const sendDataToBackend = async (data, resultWindow = null) => {
  try {
    const endpoint = isWeatherMode.value ? '/search/weather/' : '/search/flights/';
    const response = await axios.post(endpoint, {
      ...data,
      search_origin: routeNameToSearchOrigin(route.name),
      experiments: getActiveExperiments(),
    });
    // Response is now a dict with { search_hash, bag_deep_links }
    const result = response.data;
    const hash = typeof result === 'string' ? result : result.search_hash;
    const bagDeepLinks = typeof result === 'object' ? result.bag_deep_links : null;

    // Compute total checked bags across all passengers for frontend use
    const totalCheckedBags = searchForm.value.passengerDetails.reduce((sum, p) => {
      if (p.noBags) return sum;
      const checked = (p.bags || []).filter(b => b.type === 'checked_bag')
        .reduce((bagSum, b) => bagSum + (parseInt(b.numberOfBags) || 0), 0);
      return sum + checked;
    }, 0);

    // Compute total cabin bags across all passengers for frontend use
    const totalCabinBags = searchForm.value.passengerDetails.reduce((sum, p) => {
      if (p.noBags) return sum;
      const cabin = (p.bags || []).filter(b => b.type === 'cabin_bag')
        .reduce((bagSum, b) => bagSum + (parseInt(b.numberOfBags) || 0), 0);
      return sum + cabin;
    }, 0);

    // Guard: without a hash we cannot navigate to the results page (the route
    // requires :hash). Surface a clear error instead of pushing an undefined param.
    if (!hash) {
      notification.value = {
        type: 'error',
        message: 'No results found. Please try again.',
      };
      emit('search-error', 'No results found.');
      closePendingResultsTab(resultWindow);
      return;
    }

    const openedInNewTab = navigatePendingResultsTab(
      resultWindow,
      router.resolve({ name: 'Results', params: { hash } }).href,
      {
        bagDeepLinks,
        totalCheckedBags: String(totalCheckedBags),
        totalCabinBags: String(totalCabinBags),
        [`resultsSearchParams:${hash}`]: data,
      },
    );
    emit('search-results', { hash, bagDeepLinks, totalCheckedBags, totalCabinBags, openedInNewTab });
    notification.value = { type: '', message: '' };
  } catch (error) {
    closePendingResultsTab(resultWindow);
    const errorMsg = error.response?.data?.message || error.response?.data?.error || t('errors.requestError');
    notification.value = {
      type: 'error',
      message: errorMsg,
    };
    emit('search-error', errorMsg);
  }
};

// Watch for currency changes
watch(
  () => searchForm.value.currency,
  (newCurrency) => {
    if (newCurrency && !isApplyingDetectedCurrency) {
      saveCurrencyPreference(newCurrency);
    }
  }
);

watch(
  () => searchForm.value.from,
  () => {
    if (searchForm.value.from.length > 0) {
      errors.value.from = false;
    }
  }
);


watch(
  () => searchForm.value.departureDate,
  () => {
    if (searchForm.value.departureDate) {
      errors.value.departureDate = false;
    }
  }
);

watch(
  () => searchForm.value.returnDate,
  () => {
    if (searchForm.value.returnDate || searchForm.value.tripType === 'one-way') {
      errors.value.returnDate = false;
    }
  }
);

watch(
  () => searchForm.value.currency,
  () => {
    if (searchForm.value.currency) {
      errors.value.currency = false;
    }
  }
);

watch(
  () => searchForm.value.maxStops,
  (newVal) => {
    searchForm.value.maxStops = newVal;
  }
);

watch(
  errors,
  (newErrors) => {
    const hasErrors = Object.values(newErrors).some(error => error);
    if (!hasErrors && notification.value.type === 'error') {
      notification.value.message = '';
      notification.value.type = '';
    }
  },
  { deep: true }
);

const isPassengerPopupVisible = ref(false);
const globalClass = ref('economy');

const openPassengerPopup = () => {
  isPassengerPopupVisible.value = true;
};

const closePassengerPopup = () => {
  isPassengerPopupVisible.value = false;
};

// Increment/decrement passenger count quickly
const generateId = () => '_' + Math.random().toString(36).substr(2, 9);

const incrementPassengers = () => {
  if (searchForm.value.passengers < 9) {
    searchForm.value.passengers++;
    searchForm.value.passengerDetails.push({ id: generateId(), noBags: true, bags: [] });
  }
};

const decrementPassengers = () => {
  if (searchForm.value.passengers > 1) {
    searchForm.value.passengers--;
    searchForm.value.passengerDetails.pop();
  }
};

const updatePassengerDetails = (updatedDetails) => {
  if (!updatedDetails || !Array.isArray(updatedDetails.passengers)) {
    console.error('Invalid updatedDetails:', updatedDetails);
    return;
  }
  searchForm.value.passengers = updatedDetails.passengers.length;
  searchForm.value.passengerDetails.splice(
    0,
    searchForm.value.passengerDetails.length,
    ...updatedDetails.passengers
  );
  if (updatedDetails.globalClass) {
    globalClass.value = updatedDetails.globalClass;
  }
  closePassengerPopup();
};



const updateSelectedCountries = (countries) => {
  searchForm.value.selectedCountries = countries;
}

// Handler for DateRange popup closed event
const handlePopupClosed = () => {
  console.log("Popup closed - handlePopupClosed function called in SearchForm.vue");
  // Legacy scraper code removed - searches now use Kiwi API directly
};

// Programmatically apply overrides (e.g. { maxStops: 'any' }) and run the search
const submitSearch = (overrides = {}) => {
  Object.assign(searchForm.value, overrides);
  handleSearch();
};

// Apply search params without triggering a search (used by parent to sync state)
const applySearchParams = (params) => {
  if (!params) return;
  const parsed = typeof params === 'string' ? JSON.parse(params) : params;
  searchForm.value = { ...searchForm.value, ...parsed };
  applyWeatherParams(parsed);
};

defineExpose({
  updateSelectedCountries,
  handlePopupClosed,
  submitSearch,
  applySearchParams
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease, transform 1s ease;
}
.fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-leave-active {
  position: absolute;
  top: 0;
  left: 0.75rem;
  right: 0;
  width: 92%;
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.flash-bg {
  animation: flash-animation 3s infinite;
}

@keyframes flash-animation {
  0% { filter: brightness(0.98); }
  50% { filter: brightness(1.04); }
  100% { filter: brightness(0.98); }
}

@keyframes pulse-once {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
.animate-pulse-once {
  animation: pulse-once 0.8s ease-in-out 2;
}
</style>